//@flow
import { Formik } from 'formik';
import _ from 'lodash';
import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { Button } from 'reactstrap';
import connect from 'react-redux/es/connect/connect';
import * as Yup from 'yup';

import FormInput from '../../components/Inputs/FormInput';
import FormSelectInput from '../../components/Inputs/FormSelectInput';
import Toast from '../../components/Toasts/Toast';
import TopoTitleComponente from '../Topo/TopoTitleComponente';
import { clienteService } from '../../services/cliente.service';
import { estoqueInternoService } from '../../services/estoqueInterno.service';
import { produtoService } from '../../services/produto.service';
import { isSudo } from '../../helpers/permissions.helper';

const validationSchema = Yup.object().shape({
  cliente: Yup.object().required('Cliente é obrigatório'),
  produto: Yup.object().required('Produto é obrigatório'),
  quantidadeFisica: Yup.string()
    .required('Informe o estoque físico real')
    .test(
      'quantidade-valida',
      'Informe uma quantidade maior ou igual a zero',
      (value) => {
        if (value === undefined || value === null || String(value).trim() === '') {
          return false;
        }

        const quantidade = Number(String(value).replace(',', '.'));
        return Number.isFinite(quantidade) && quantidade >= 0;
      },
    ),
  justificativa: Yup.string()
    .trim()
    .min(3, 'Informe uma justificativa')
    .required('Justificativa é obrigatória'),
  observacao: Yup.string().nullable(),
});

const initialValues = {
  cliente: null,
  produto: null,
  quantidadeFisica: '',
  justificativa: '',
  observacao: '',
};

function extrairLista(response) {
  const data = response && response.data ? response.data : response;
  return Array.isArray(data) ? data : [];
}

function formatarQuantidade(value) {
  if (value === null || value === undefined) {
    return '-';
  }

  return Number(value).toLocaleString('pt-BR', {
    maximumFractionDigits: 6,
  });
}

function extrairMensagemErro(error) {
  const data = _.get(error, 'response.data');

  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  return (
    _.get(data, 'message') ||
    _.get(data, 'detalhe') ||
    'Não foi possível ajustar o estoque interno.'
  );
}

export class EstoqueInternoAjuste extends React.PureComponent {
  state = {
    saldo: null,
    carregandoSaldo: false,
  };

  saldoRequestId = 0;

  buscarClientesInternos = () => {
    return clienteService.findAll().then((response) =>
      extrairLista(response).filter(
        (cliente) => cliente.modoControleEstoque === 'INTERNO',
      ),
    );
  };

  limparSelecaoProduto = (setFieldValue) => {
    setFieldValue('produto', null);
    setFieldValue('quantidadeFisica', '');
    this.saldoRequestId += 1;
    this.setState({ saldo: null, carregandoSaldo: false });
  };

  carregarSaldo = (cliente, produto) => {
    const requestId = ++this.saldoRequestId;

    if (!cliente || !produto) {
      this.setState({ saldo: null, carregandoSaldo: false });
      return;
    }

    this.setState({ saldo: null, carregandoSaldo: true });

    estoqueInternoService
      .consultarSaldo(cliente.codigoCliente, produto.id)
      .then((response) => {
        if (requestId === this.saldoRequestId) {
          this.setState({
            saldo: response && response.data ? response.data : null,
            carregandoSaldo: false,
          });
        }
      })
      .catch((error) => {
        if (requestId === this.saldoRequestId) {
          this.setState({ saldo: null, carregandoSaldo: false });
          this.props.error({ message: extrairMensagemErro(error) });
        }
      });
  };

  renderSaldo = () => {
    const { saldo, carregandoSaldo } = this.state;

    if (carregandoSaldo) {
      return <p className="text-muted mb-0">Consultando saldo atual...</p>;
    }

    if (!saldo) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12 col-md-4 mb-3">
            <small className="text-muted d-block">Estoque físico atual</small>
            <strong>{formatarQuantidade(saldo.quantidadeFisica)}</strong>
          </div>
          <div className="col-12 col-md-4 mb-3">
            <small className="text-muted d-block">Estoque reservado</small>
            <strong>{formatarQuantidade(saldo.quantidadeReservada)}</strong>
          </div>
          <div className="col-12 col-md-4 mb-3">
            <small className="text-muted d-block">Estoque disponível</small>
            <strong
              className={
                Number(saldo.quantidadeDisponivel) < 0 ? 'text-danger' : ''
              }
            >
              {formatarQuantidade(saldo.quantidadeDisponivel)}
            </strong>
          </div>
        </div>

        {saldo.inconsistente && (
          <div className="alert alert-warning mb-0">
            Este saldo está marcado como inconsistente e exige conferência.
          </div>
        )}
      </React.Fragment>
    );
  };

  render() {
    if (!isSudo()) {
      return <Redirect to="/dashboard" />;
    }

    return (
      <React.Fragment>
        <TopoTitleComponente
          mainTitle="Ajuste de Estoque Interno"
          subTitle="Conferência manual"
          canBack={true}
        />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={async (values, actions) => {
            const confirmou = window.confirm(
              `Confirmar ajuste do produto ${values.produto.codigoProduto} para ${values.quantidadeFisica}?`,
            );

            if (!confirmou) {
              actions.setSubmitting(false);
              return;
            }

            this.props.loading(true);

            try {
              const response = await estoqueInternoService.ajustarSaldoManual({
                codigoCliente: values.cliente.codigoCliente,
                produtoId: values.produto.id,
                quantidadeFisica: String(values.quantidadeFisica).replace(',', '.'),
                justificativa: values.justificativa.trim(),
                observacao: values.observacao ? values.observacao.trim() : '',
              });

              this.setState({
                saldo: response && response.data ? response.data : null,
                carregandoSaldo: false,
              });
              actions.setSubmitting(false);
              this.props.success({
                message: 'Estoque interno ajustado com sucesso.',
              });
            } catch (error) {
              actions.setSubmitting(false);
              this.props.error({ message: extrairMensagemErro(error) });
            } finally {
              this.props.loading(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit} noValidate>
              <div className="flex-column">
                <Toast />

                <div className="cardSeculus d-flex flex-column mb-3">
                  <div className="topoCard d-flex flex-column flex-sm-row">
                    <h3 className="flex-grow-1">Produto para ajuste</h3>
                  </div>

                  <div className="form-row">
                    <div className="form-group col-12 col-md-6">
                      <FormSelectInput
                        name="cliente"
                        id="clienteEstoqueInterno"
                        label="Cliente com estoque interno"
                        placeholder="Selecione o cliente"
                        valueKey="codigoCliente"
                        labelKey="label"
                        required={true}
                        returnFullObject={true}
                        searchable={true}
                        service={this.buscarClientesInternos}
                        onChange={(name, value) => {
                          setFieldValue(name, value);
                          this.limparSelecaoProduto(setFieldValue);
                        }}
                        value={values.cliente}
                        messageValidate={touched.cliente && errors.cliente}
                      />
                    </div>

                    <div className="form-group col-12 col-md-6">
                      <FormSelectInput
                        key={
                          (values.cliente && values.cliente.codigoCliente) ||
                          'produtoEstoqueInterno'
                        }
                        name="produto"
                        id="produtoEstoqueInterno"
                        label="Produto"
                        placeholder="Digite código ou descrição"
                        labelKey="label"
                        valueKey="id"
                        parent={values.cliente}
                        disabled={!values.cliente}
                        required={true}
                        returnFullObject={true}
                        searchable={true}
                        minSearchLength={3}
                        debounceMs={500}
                        onFetchData={produtoService.buscarPorCliente}
                        onChange={(name, value) => {
                          setFieldValue(name, value);
                          setFieldValue('quantidadeFisica', '');
                          this.carregarSaldo(values.cliente, value);
                        }}
                        value={values.produto}
                        messageValidate={touched.produto && errors.produto}
                      />
                    </div>
                  </div>

                  {values.cliente && values.produto && (
                    <div className="border rounded p-3 mb-3">
                      <h5>
                        {values.produto.codigoProduto} - {values.produto.descricao}
                      </h5>
                      <p className="text-muted mb-3">
                        Unidade de medida: {values.produto.unidadeMedida || '-'}
                      </p>
                      {this.renderSaldo()}
                    </div>
                  )}

                  <div className="form-row">
                    <FormInput
                      label="Estoque físico real"
                      placeholder="Informe a quantidade encontrada"
                      id="quantidadeFisica"
                      name="quantidadeFisica"
                      type="number"
                      min="0"
                      step="any"
                      required={true}
                      value={values.quantidadeFisica}
                      onChange={setFieldValue}
                      messageValidate={
                        touched.quantidadeFisica && errors.quantidadeFisica
                      }
                    />

                    <FormInput
                      label="Justificativa"
                      placeholder="Motivo da conferência/ajuste"
                      id="justificativaAjusteEstoque"
                      name="justificativa"
                      type="text"
                      required={true}
                      value={values.justificativa}
                      onChange={setFieldValue}
                      messageValidate={touched.justificativa && errors.justificativa}
                    />

                    <FormInput
                      label="Observação"
                      placeholder="Informação adicional (opcional)"
                      id="observacaoAjusteEstoque"
                      name="observacao"
                      type="text"
                      required={false}
                      value={values.observacao}
                      onChange={setFieldValue}
                      messageValidate={touched.observacao && errors.observacao}
                    />
                  </div>

                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      color="primary"
                      type="submit"
                      disabled={isSubmitting || !values.produto}
                    >
                      Salvar ajuste
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </React.Fragment>
    );
  }
}

const mapDispatch = ({ alert: { success, error }, load: { loading } }) => ({
  success: (message) => success(message),
  error: (message) => error(message),
  loading: (value) => loading({ load: value }),
});

export default connect(null, mapDispatch)(EstoqueInternoAjuste);
